import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { otcService, toPublicOrderbookParams } from '../services/otcService'
import { OrderbookItem, OrderbookQueryParams, OrderType, DealType, SortBy, SortDirection, OrderbookStatsResponse } from '../types/otc'
import { tradingService } from '@/services/tradingService';
import { dataRoomService } from '@/services/dataRoomService';
import { useAccessTier } from '@/hooks/useAccessTier';

// Query keys for cache management
export const otcKeys = {
  all: ['otc'] as const,
  // 🔴 The tier is part of the key. Without it, a user who completes phase 1
  // in this tab would keep serving the gated answer out of the cache, and a
  // user who logged out and back in as someone else would inherit the previous
  // session's data. The key must name every input to the response, and the
  // caller's entitlement is one of them.
  stats: (tier?: string) => [...otcKeys.all, 'stats', tier ?? 'unknown'] as const,
  orders: (params?: OrderbookQueryParams) => [...otcKeys.all, 'orders', params] as const,
}

export const projectsKeys = {
  all: ['projects'] as const,
  stats: () => [...projectsKeys.all, 'stats'] as const,
}

/**
 * The orderbook listing, from whichever endpoint the caller is entitled to.
 *
 * 🔴 The tier chooses the ENDPOINT. It does not choose what to hide from a
 * response that already contains the numbers:
 *
 *   full   → `GET /orderbook`         rows with `offered_amount` and FDV
 *   browse → `GET /orderbook/public`  rows with neither, `financials_hidden: true`
 *
 * While the tier is `unresolved` this hook fetches NOTHING and reports
 * `loading: true`. That is what stops the "flash of real data" — an eager fetch
 * plus a later gate would paint the true numbers for one frame, and one frame
 * is a screenshot.
 */
export const useOTC = (params?: OrderbookQueryParams) => {
  const { tier, canReadFinancials } = useAccessTier()
  const [orders, setOrders] = useState<OrderbookItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [total, setTotal] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const paramsRef = useRef(params)
  const tierRef = useRef(tier)

  // Memoize params to create stable reference
  const stableParams = useMemo(() => params, [
    params?.limit,
    params?.offset,
    params?.search,
    params?.type,
    params?.deal_type,
    params?.industry,
    params?.sort_by,
    params?.sort_direction
  ])

  const fetchOrders = useCallback(async (page = 1, customParams?: OrderbookQueryParams) => {
    // Never fetch on an unresolved tier: we do not yet know which endpoint the
    // caller is entitled to, and guessing means guessing wrong half the time.
    if (tier === 'unresolved') return

    try {
      setLoading(true)
      setError(null)

      const activeParams = customParams || stableParams
      const limit = activeParams?.limit || 10
      const offset = (page - 1) * limit

      const queryParams = {
        ...activeParams,
        limit,
        offset
      }

      const response = canReadFinancials
        ? await otcService.getOrderbook(queryParams)
        : await otcService.getPublicOrderbook(toPublicOrderbookParams(queryParams))

      setOrders(response.data)
      setTotal(response.total)
      setCurrentPage(page)
      setTotalPages(Math.ceil(response.total / limit))

    } catch (err) {
      // 🔴 Fail CLOSED. Leaving the previous page's rows on screen after a
      // failed refetch would keep showing whatever the last successful call
      // returned — including real numbers fetched before a tier change.
      setOrders([])
      setTotal(0)
      setTotalPages(1)
      setError(err instanceof Error ? err.message : 'Failed to fetch orders')
    } finally {
      setLoading(false)
    }
  }, [stableParams, tier, canReadFinancials])

  useEffect(() => {
    if (tier === 'unresolved') {
      // Hold on the loading state rather than rendering an empty table that
      // reads as "no orders".
      setLoading(true)
      return
    }

    // Refetch when the params change OR when the caller's entitlement does —
    // completing phase 1 in this tab has to swap the endpoint under a mounted
    // table, and these hooks have no cache key to invalidate.
    const paramsChanged = JSON.stringify(paramsRef.current) !== JSON.stringify(stableParams)
    const tierChanged = tierRef.current !== tier
    if (!paramsChanged && !tierChanged && orders.length > 0) {
      return
    }

    paramsRef.current = stableParams
    tierRef.current = tier
    fetchOrders(1)
  }, [stableParams, fetchOrders, orders.length, tier])

  const goToPage = useCallback((page: number) => {
    if (page >= 1 && page <= totalPages && !loading) {
      fetchOrders(page)
    }
  }, [totalPages, loading, fetchOrders])

  const refresh = useCallback(() => {
    fetchOrders(currentPage)
  }, [fetchOrders, currentPage])

  return {
    orders,
    loading,
    error,
    total,
    currentPage,
    totalPages,
    goToPage,
    refresh
  }
}

// Hook for managing Secondaries orderbook filters with URL query params
export const useOTCFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const filters = {
    search: searchParams.get('search') || '',
    type: (searchParams.get('type') as OrderType) || '',
    assetType: (searchParams.get('assetType') as DealType) || '',
    trancheSize: searchParams.get('trancheSize') || '',
    sortBy: (searchParams.get('sortBy') as SortBy) || '',
    sortDirection: (searchParams.get('sortDirection') as SortDirection) || ''
  }

  const updateFilter = useCallback((key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams)
    if (value) {
      newParams.set(key, value)
    } else {
      newParams.delete(key)
    }
    setSearchParams(newParams)
  }, [searchParams, setSearchParams])

  const setters = {
    setSearch: (value: string) => updateFilter('search', value),
    setType: (value: OrderType | '') => updateFilter('type', value),
    setAssetType: (value: DealType | '') => updateFilter('assetType', value),
    setTrancheSize: (value: string) => updateFilter('trancheSize', value),
    setSortBy: (value: SortBy | '') => updateFilter('sortBy', value),
    setSortDirection: (value: SortDirection | '') => updateFilter('sortDirection', value),
  }

  return { filters, ...setters }
}

export * from './useOTC';

/**
 * Hook for fetching Secondaries orderbook stats with React Query
 * - Automatic caching (5 min stale time)
 * - Shared cache across components (Header, MainScreen, etc.)
 */
export function useOtcStats() {
  const { tier, canReadFinancials } = useAccessTier();

  const { data, isLoading, error } = useQuery({
    queryKey: otcKeys.stats(tier),
    queryFn: async () => {
      const response = await tradingService.getOrderbookStats();
        // API returns: { data: { total_projects, total_orderbook_value, ... } }
        // Extract data from the nested response structure
      return (response as { data?: OrderbookStatsResponse })?.data || response;
    },
    // 🔴 `/orderbook/stats` is every headline money figure on the dashboard and
    // has no non-financial counterpart, so a gated caller does not ask for it
    // at all. There is nothing to blur, because nothing was fetched.
    enabled: canReadFinancials,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 20 * 1000,
    refetchIntervalInBackground: true,
  });

  return {
    data: data ?? null,
    // A disabled query is not "loading" to React Query, but the dashboard's
    // skeletons must not run forever for a gated user either — so report
    // loading only while the tier is still unknown or a real request is in
    // flight.
    loading: tier === 'unresolved' || (canReadFinancials && isLoading),
    /** No stats will ever arrive for this session. Render the gate, not a zero. */
    gated: tier !== 'unresolved' && !canReadFinancials,
    error: error instanceof Error ? error.message : error ? String(error) : null
  };
}

/**
 * Hook for fetching projects stats with React Query
 * - Automatic caching (5 min stale time)
 */
export function useProjectsStats() {
  const { tier, canReadFinancials } = useAccessTier();

  const { data, isLoading, error } = useQuery({
    queryKey: [...projectsKeys.stats(), tier],
    queryFn: () => dataRoomService.getProjectsStats(),
    // Defence in depth. The data-room routes that mount this are already
    // route-gated for a browse-tier session (see `AppContent`), so this should
    // be unreachable — belt and braces, because a future route change must not
    // silently open a funding-round valuation feed.
    enabled: canReadFinancials,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    data: data ?? null,
    loading: tier === 'unresolved' || (canReadFinancials && isLoading),
    error: error instanceof Error ? error.message : error ? String(error) : null
  };
}
